import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';

const sidebarGlassStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(64px) saturate(200%)',
    WebkitBackdropFilter: 'blur(64px) saturate(200%)',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
    borderRight: '0 solid transparent',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.14), inset 0 -1px 0 rgba(255, 255, 255, 0.03)',
    transition: 'width 700ms cubic-bezier(0.2, 0.8, 0.2, 1), transform 700ms cubic-bezier(0.2, 0.8, 0.2, 1), background-color 700ms cubic-bezier(0.2, 0.8, 0.2, 1), border-color 700ms cubic-bezier(0.2, 0.8, 0.2, 1)'
};

const Layout = ({ children, isAdmin, isSuperAdmin, handleLogout, userEmail }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogoClick = () => {
        navigate('/dashboard');
    };

    const navItems = [
        {
            label: 'Overview', path: '/overview', icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.592 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                </svg>
            )
        }
    ];

    if (isAdmin) {
        navItems.push(
            {
                label: 'Operations', path: '/admin', icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                )
            }
        );
    }

    if (isSuperAdmin) {
        navItems.push(
            {
                label: 'Audit Logs', path: '/admin/audit-logs', icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                    </svg>
                )
            }
        );
    }

    navItems.push(
        {
            label: 'Support', path: '/support', icon: (
                <svg
                    className="h-6 w-6"
                    viewBox="0 0 360 360"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                >
                    <g>
                        {/* Central Hub */}
                        <path
                            fill="currentColor"
                            stroke="none"
                            d="M217.92,143.51c-23.32-11.4-51.47-1.74-62.87,21.58c-11.4,23.32-1.74,51.47,21.58,62.87
                                c23.32,11.4,51.47,1.74,62.87-21.58C250.9,183.05,241.24,154.91,217.92,143.51z"
                        />
                        {/* Outer Nodes (formerly orange) */}
                        <path fill="currentColor" stroke="none" d="M163.37,7.17c-6.6-2.37-13.87,1.07-16.24,7.67c-2.37,6.6,1.07,13.87,7.67,16.24
                            c6.6,2.37,13.87-1.07,16.24-7.67C173.41,16.81,169.98,9.54,163.37,7.17z"/>
                        <path fill="currentColor" stroke="none" d="M42.8,185.19c-5.7-4.08-13.64-2.77-17.72,2.93c-4.08,5.71-2.77,13.64,2.93,17.72
                            c5.7,4.08,13.64,2.77,17.72-2.93C49.81,197.21,48.5,189.28,42.8,185.19z"/>
                        <path fill="currentColor" stroke="none" d="M131.44,331.17c-6.3-3.08-13.91-0.47-16.98,5.83c-3.08,6.3-0.47,13.91,5.83,16.99
                            c6.3,3.08,13.91,0.47,16.99-5.83C140.36,341.86,137.75,334.26,131.44,331.17z"/>
                        <path fill="currentColor" stroke="none" d="M315.51,354.1c6.3,3.08,13.91,0.47,16.99-5.83c3.08-6.3,0.47-13.9-5.83-16.99
                            c-6.3-3.08-13.91-0.47-16.99,5.83C306.6,343.42,309.21,351.02,315.51,354.1z"/>
                        <path fill="currentColor" stroke="none" d="M320.94,127.76c6.14,3.39,13.87,1.15,17.25-4.99c3.38-6.14,1.15-13.86-4.99-17.25
                            c-6.14-3.39-13.87-1.16-17.25,4.99C312.56,116.65,314.79,124.37,320.94,127.76z"/>
                        {/* Spokes (formerly gray stroke) */}
                        <polygon fill="currentColor" stroke="none" points="167.61,39.84 187.58,126.7 182.64,127.79 162.75,41.28"/>
                        <polygon fill="currentColor" stroke="none" points="57.85,195.72 137.24,188.75 136.88,183.71 57.3,190.7"/>
                        <polygon fill="currentColor" stroke="none" points="250.07,156.25 252.39,161.08 309.48,130.58 306.68,126"/>
                        <polygon fill="currentColor" stroke="none" points="234.11,233.41 306.02,325.92 310.4,323.02 238.14,230.06"/>
                        <polygon fill="currentColor" stroke="none" points="133,321.28 138.89,324.11 176.22,242.32 170.34,239.48"/>
                    </g>
                </svg>
            )
        }
    );

    return (
        <div className="flex h-screen w-full bg-[#050505] text-white overflow-hidden font-[Inter] selection:bg-red-500/30 relative">

            {/* Mobile Sidebar Overlay Backdrop */}
            {mobileMenuOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar (Floating Glass Dock) */}
            <aside className={`
                fixed md:relative z-50 h-full flex flex-col items-center pt-0 pb-8 gap-8 group/sidebar
                w-20 hover:w-32
                ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}
            style={sidebarGlassStyle}>
                {/* Hardware Crest (Logo Integration) */}
                <div
                    className="relative h-16 w-full cursor-pointer z-50 overflow-hidden"
                    onClick={handleLogoClick}
                    title="Go to Dashboard"
                >
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative h-10 w-[96px]">
                            <img
                                src="/images/tcb-logo.png"
                                alt="TCB Logo"
                                className="absolute left-1/2 top-1/2 h-8 w-[96px] max-w-none -translate-x-1/2 -translate-y-1/2 object-contain filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] transition-[transform,opacity,filter] duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] scale-[0.9] opacity-95 group-hover/sidebar:scale-100 group-hover/sidebar:opacity-100"
                            />
                        </div>
                    </div>
                </div>


                {/* Nav Items */}
                <nav className="flex-1 flex flex-col gap-4 w-full items-center">
                    {navItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={`
                                    relative self-stretch mx-4 flex flex-col items-center justify-center py-2.5 rounded-xl transition-all duration-300 group
                                    ${isActive
                                        ? 'text-white'
                                        : 'text-white/50 hover:bg-white/[0.03] hover:text-white/80'
                                    }
                                `}
                                style={isActive ? {
                                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15), inset 0 -1px 0 rgba(0, 0, 0, 0.2)'
                                } : {}}
                            >
                                {/* Icon — scales up on sidebar hover */}
                                <div className="group-hover/sidebar:scale-[1.15]" style={{ transition: 'transform 700ms cubic-bezier(0.2, 0.8, 0.2, 1)', filter: isActive ? 'drop-shadow(0 0 8px rgba(255,255,255,0.4))' : 'none' }}>
                                    {item.icon}
                                </div>

                                {/* Label — fades in below icon on sidebar hover */}
                                <span className="text-[11px] font-semibold uppercase tracking-[0.15em] mt-1.5 opacity-0 max-h-0 overflow-hidden group-hover/sidebar:opacity-100 group-hover/sidebar:max-h-6 whitespace-nowrap" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif', transition: 'opacity 800ms cubic-bezier(0.2, 0.8, 0.2, 1) 200ms, max-height 800ms cubic-bezier(0.2, 0.8, 0.2, 1) 200ms' }}>
                                    {item.label}
                                </span>

                                {/* Active Indicator */}
                                {isActive && (
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-red-500 rounded-r-full" style={{ boxShadow: '4px 0 12px rgba(255, 60, 60, 0.5), inset 2px 0 4px rgba(255, 255, 255, 0.4)' }}></div>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Logout */}
                <button
                    onClick={handleLogout}
                    className="self-stretch mx-4 flex flex-col items-center justify-center py-2.5 rounded-xl text-white/50 hover:bg-red-500/10 hover:text-red-500 transition-all duration-300 group relative"
                >
                    <div className="group-hover/sidebar:scale-[1.15]" style={{ transition: 'transform 700ms cubic-bezier(0.2, 0.8, 0.2, 1)' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                        </svg>
                    </div>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.15em] mt-1.5 opacity-0 max-h-0 overflow-hidden group-hover/sidebar:opacity-100 group-hover/sidebar:max-h-6 whitespace-nowrap text-red-400" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif', transition: 'opacity 600ms cubic-bezier(0.2, 0.8, 0.2, 1) 150ms, max-height 600ms cubic-bezier(0.2, 0.8, 0.2, 1) 150ms' }}>
                        Logout
                    </span>
                </button>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 h-full w-full relative z-10 overflow-hidden flex flex-col">
                <header className="flex-shrink-0 h-16 w-full flex items-center justify-between px-4 md:px-8 header-glass">

                    {/* Left: Hamburger + Company Name + Brand Logos */}
                    <div className="flex items-center gap-3 md:gap-6">
                        {/* Mobile Hamburger Toggle */}
                        <button
                            className="p-2 -ml-2 text-white/70 hover:text-white md:hidden"
                            onClick={() => setMobileMenuOpen(true)}
                        >
                            <Menu size={24} />
                        </button>

                        <img
                            src="/images/MJG_Logo.png"
                            alt="McJunkins Group"
                            className="h-5 md:h-8 w-auto object-contain brightness-0 invert opacity-95"
                        />

                        {/* Divider */}
                        <div className="hidden md:block h-8 w-[1px] bg-white/10" />

                        {/* Brand Logos */}
                        <div className="hidden sm:flex items-center gap-4">
                            {/* Walk-On's Logo */}
                            <div className="h-9 w-auto px-2 py-1 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all duration-300 group">
                                <img
                                    src="/images/walk-ons-logo.jpg"
                                    alt="Walk-On's"
                                    className="h-6 w-auto object-contain transition-opacity"
                                />
                            </div>
                            {/* Smalls Logo */}
                            <div className="h-9 w-auto px-2 py-1 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all duration-300 group">
                                <img
                                    src="/images/SmallSlidersLogo.png"
                                    alt="Smalls"
                                    className="h-6 w-auto object-contain transition-opacity"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right: Date Display */}
                    <div className="hidden md:flex items-center gap-4 text-sm font-mono text-white/60 tracking-tight">
                        {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                </header>

                {/* Page Content Container */}
                <div className="flex-1 overflow-hidden relative">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;
